import type { AgentEvent, CartView, SessionState } from './types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });

  const text = await response.text();
  const body = text.length > 0 ? JSON.parse(text) : {};

  if (!response.ok) {
    throw new ApiError(body.message ?? `HTTP ${response.status}`, response.status, body.error);
  }
  return body as T;
}

export const api = {
  accessState: () => request<{ granted: boolean }>('/access'),
  login: (password: string) =>
    request<{ ok: true }>('/access', { method: 'POST', body: JSON.stringify({ password }) }),
  session: () => request<SessionState>('/session'),
  newSession: () => request<{ sessionId: string; cart: CartView }>('/session/new', { method: 'POST' }),
  addItem: (productId: string, quantity: number) =>
    request<{ cart: CartView }>('/cart/items', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity }),
    }),
  setQuantity: (productId: string, quantity: number) =>
    request<{ cart: CartView }>(`/cart/items/${encodeURIComponent(productId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity }),
    }),
  removeItem: (productId: string) =>
    request<{ cart: CartView }>(`/cart/items/${encodeURIComponent(productId)}`, { method: 'DELETE' }),
  setBuyer: (payload: unknown) =>
    request<{ cart: CartView }>('/buyer', { method: 'PUT', body: JSON.stringify(payload) }),
  confirm: (cartVersion: string) =>
    request<{ cart: CartView }>('/confirm', {
      method: 'POST',
      body: JSON.stringify({ cart_version: cartVersion }),
    }),
  startPayment: () => request<{ cart: CartView }>('/payment/start', { method: 'POST' }),
  paymentStatus: () => request<{ cart: CartView }>('/payment/status'),
  cancel: () => request<{ cart: CartView }>('/cancel', { method: 'POST' }),
  technical: () => request<Record<string, unknown>>('/technical'),
};

/** Streams the assistant turn. SSE over POST, read via the fetch body stream. */
export async function streamChat(
  message: string,
  onEvent: (event: AgentEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const response = await fetch('/api/chat', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
    signal,
  });

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    let parsed: { message?: string } = {};
    try {
      parsed = JSON.parse(text);
    } catch {
      /* ignore */
    }
    throw new ApiError(parsed.message ?? `HTTP ${response.status}`, response.status);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const payload = frame.replace(/^data: /m, '').trim();
      if (payload.length > 0) {
        try {
          onEvent(JSON.parse(payload) as AgentEvent);
        } catch {
          /* skip malformed frame */
        }
      }
      boundary = buffer.indexOf('\n\n');
    }
  }
}

export const formatCents = (cents: number | null, currency = 'EUR'): string =>
  cents === null
    ? '–'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(cents / 100);
